/**
 * Phase 1 portal: shapes and pure helpers shared by the audit list, the audit detail page and the Phase-1 home.
 * Everything here is unit tested (audit-view.test.ts); no network, no React.
 */
import { SEVERITIES, SIGNAL_IDS, type AuditAnalysis, type Finding, type LocalizedText, type SignalId } from '@qm/contracts/audit-analysis';

/** GET /v1/growth-audit/jobs[/:id] (qm-audit JobView). */
export interface AuditJob {
  id: string;
  status: 'in_progress' | 'delayed' | 'ready';
  stage: string;
  tier: string;
  locale: string;
  targetUrl: string;
  createdAt: string;
  artifactsReadyAt: string | null;
  deliveredAt: string | null;
  artifacts: { kind: 'pdf' | 'xlsx'; size: number; filename: string }[];
  rerunOf?: string | null;
  includedReruns?: { included: number; used: number; remaining: number; canRerun: boolean; reason: string | null; until: string | null } | null;
  /** a report a Quixo workspace shared with this workspace: view and download only */
  sharedWithYou?: boolean;
}

/** GET /v1/growth-audit/jobs/:id/report */
export interface AuditReport { job: AuditJob; analysis: AuditAnalysis }

/** GET /v1/growth-audit/entitlements */
export interface UnusedEntitlement { id: string; sku: string; tier: string | null; type: 'paid' | 'complimentary'; orderId: string | null; website: string | null; createdAt: string; expiresAt: string | null }

/** GET /v1/growth-audit/orders */
export interface AuditOrder {
  id: string; sku: string; tier: string | null; website: string | null; status: 'awaiting_payment' | 'paid' | 'payment_failed' | 'refunded' | 'expired' | 'free';
  amountMinor: number; currency: string; createdAt: string; paidAt: string | null; via: 'website' | 'portal';
}

/** Billing says `pro`, the pipeline says `professional` (G18): one key for both. */
export function tierKey(tier: string | null | undefined): 'essential' | 'professional' | 'enterprise' | null {
  if (tier === 'pro' || tier === 'professional' || tier === 'audit_pro') return 'professional';
  if (tier === 'essential' || tier === 'audit_essential') return 'essential';
  if (tier === 'enterprise' || tier === 'audit_enterprise') return 'enterprise';
  return null;
}

/** Billing tier id for a checkout (portal-checkout takes essential | pro | enterprise). */
export const checkoutTier = (k: ReturnType<typeof tierKey>): 'essential' | 'pro' | 'enterprise' => (k === 'professional' ? 'pro' : k ?? 'pro');

/** The next tier up, or null at the top. */
export function upgradeTierFor(tier: string | null | undefined): 'pro' | 'enterprise' | null {
  const k = tierKey(tier);
  return k === 'essential' ? 'pro' : k === 'professional' ? 'enterprise' : null;
}

const SOCIAL_HOSTS: Record<string, string> = {
  'instagram.com': 'instagram', 'facebook.com': 'facebook', 'tiktok.com': 'tiktok', 'threads.net': 'threads', 'threads.com': 'threads',
  'youtube.com': 'youtube', 'linkedin.com': 'linkedin', 'x.com': 'x', 'twitter.com': 'x', 'snapchat.com': 'snapchat',
};

/**
 * What the audit is about, for lists and headings: the website host, or "@handle" (platform) for a social-only subject.
 * The analysis subject wins when it is there.
 */
export function subjectLabel(targetUrl: string, analysis?: Pick<AuditAnalysis, 'subject'> | null): { label: string; platform: string | null } {
  const s = analysis?.subject;
  if (s?.kind === 'social' && !s.domain && s.profiles[0]) return { label: `@${s.profiles[0].handle.replace(/^@/, '')}`, platform: s.profiles[0].platform };
  if (s?.domain) return { label: s.domain, platform: null };
  try {
    const u = new URL(targetUrl);
    const host = u.hostname.toLowerCase().replace(/^(www\.|m\.)/, '');
    const platform = SOCIAL_HOSTS[host];
    if (platform) {
      const handle = u.pathname.split('/').filter(Boolean)[0]?.replace(/^@/, '');
      if (handle) return { label: `@${handle}`, platform };
    }
    return { label: host, platform: null };
  } catch {
    return { label: targetUrl, platform: null };
  }
}

/** Text in the reader's language; Arabic falls back to English (marked so the page can set lang="en"). */
export function textFor(t: LocalizedText | null | undefined, locale: 'en' | 'ar'): { text: string; lang: 'en' | 'ar' } | null {
  if (!t) return null;
  if (locale === 'ar' && t.ar) return { text: t.ar, lang: 'ar' };
  return { text: t.en, lang: 'en' };
}

const SEV_RANK = Object.fromEntries(SEVERITIES.map((s, i) => [s, i])) as Record<Finding['severity'], number>;

/** The fixes to do first: quick wins in plan order, then by severity; at most `n`. */
export function topFixes(a: Pick<AuditAnalysis, 'findings' | 'plan'>, n = 5): Finding[] {
  const byId = new Map(a.findings.map((f) => [f.id, f]));
  const out: Finding[] = [];
  for (const id of a.plan.quickWins) { const f = byId.get(id); if (f && !out.includes(f)) out.push(f); }
  const rest = [...a.findings].filter((f) => !out.includes(f)).sort((x, y) => SEV_RANK[x.severity] - SEV_RANK[y.severity]);
  return [...out, ...rest].slice(0, n);
}

/** The six signals in website order (the contract guarantees each exactly once). */
export function signalBoard(a: Pick<AuditAnalysis, 'signals'>): AuditAnalysis['signals'] {
  return SIGNAL_IDS.map((id: SignalId) => a.signals.find((s) => s.id === id)!).filter(Boolean);
}

export type TimelineStep = 'ordered' | 'analysing' | 'report' | 'delivered';
export interface TimelineEntry { step: TimelineStep; state: 'done' | 'current' | 'pending' | 'delayed'; at: string | null }

/** Status timeline of one audit for the detail page. */
export function statusTimeline(j: Pick<AuditJob, 'status' | 'createdAt' | 'artifactsReadyAt' | 'deliveredAt'>): TimelineEntry[] {
  const ready = j.status === 'ready';
  const reportAt = j.artifactsReadyAt ?? (ready ? j.deliveredAt : null);
  return [
    { step: 'ordered', state: 'done', at: j.createdAt },
    { step: 'analysing', state: ready ? 'done' : j.status === 'delayed' ? 'delayed' : 'current', at: null },
    { step: 'report', state: reportAt ? 'done' : 'pending', at: reportAt },
    { step: 'delivered', state: j.deliveredAt ? 'done' : ready ? 'current' : 'pending', at: j.deliveredAt },
  ];
}

/** Grade -> UI tone. */
export const gradeTone = (g: string): 'success' | 'accent' | 'warning' | 'error' | 'neutral' =>
  g === 'A' || g === 'B' ? 'success' : g === 'C' ? 'accent' : g === 'D' ? 'warning' : g === 'F' ? 'error' : 'neutral';

export const severityTone = (s: Finding['severity']): 'error' | 'warning' | 'accent' | 'neutral' =>
  s === 'critical' ? 'error' : s === 'high' ? 'warning' : s === 'medium' ? 'accent' : 'neutral';

/** Map an API error to an i18n key (G15): audit-specific reasons get their own copy, the rest the generic one. */
export function auditErrorKey(e: unknown, fallback = 'shell.error.action'): string {
  const env = (e as { envelope?: { error?: { code?: string; details?: Record<string, unknown> } } } | null)?.envelope?.error;
  const code = typeof env?.details?.code === 'string' ? env.details.code : typeof env?.details?.reason === 'string' ? env.details.reason : env?.code;
  const known = new Set([
    'rerun_not_included', 'rerun_not_finished', 'rerun_in_progress', 'rerun_limit_reached', 'rerun_window_ended', 'audit_not_ready', 'report_not_ready',
    'report_not_available', 'growth_audit_not_configured', 'growth_audit_unavailable', 'forbidden', 'currency_not_offered',
  ]);
  return code && known.has(code) ? `shell.p1.err.${code}` : fallback;
}

/** A Stripe Checkout URL we may redirect to (never an arbitrary URL from a response). */
export const isStripeCheckout = (u: unknown): u is string => typeof u === 'string' && /^https:\/\/checkout\.stripe\.com\//.test(u);
